import { MinusIcon, PlusIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";

interface NumberInputProps {
  value?: number;
  onChange?: (value: number) => void;
  min?: number;
  max?: number;
  className?: string;
  label?: string;
  id?: string;
}

export const NumberInput = ({
  value = 0,
  onChange,
  min = 0,
  max,
  className,
  label,
  id,
}: NumberInputProps) => {
  const [inputValue, setInputValue] = useState(value.toString());

  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  const handleIncrement = () => {
    const newValue = value + 1;
    if (max === undefined || newValue <= max) {
      onChange?.(newValue);
    }
  };

  const handleDecrement = () => {
    const newValue = value - 1;
    if (min === undefined || newValue >= min) {
      onChange?.(newValue);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);

    const numVal = parseInt(val);
    if (!isNaN(numVal)) {
      if (
        (min === undefined || numVal >= min) &&
        (max === undefined || numVal <= max)
      ) {
        onChange?.(numVal);
      }
    }
  };

  const handleBlur = () => {
    // Reset to current value if invalid
    setInputValue(value.toString());
  };

  return (
    <div className={cn("w-full space-y-2", className)}>
      {label && <div className="text-xs">{label}</div>}
      <div className="flex h-8 w-full items-center overflow-hidden rounded-md border border-input bg-transparent shadow-2xs">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleDecrement}
          disabled={min !== undefined && value <= min}
          className="h-full aspect-square rounded-none border-r hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
        >
          <MinusIcon className="size-4" />
          <span className="sr-only">Decrement</span>
        </Button>
        <Input
          id={id}
          type="number"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          min={min}
          max={max}
          className="h-full flex-1 border-0 text-xs text-center tabular-nums outline-hidden focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleIncrement}
          disabled={max !== undefined && value >= max}
          className="h-full aspect-square rounded-none border-l hover:bg-accent hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
        >
          <PlusIcon className="size-4" />
          <span className="sr-only">Increment</span>
        </Button>
      </div>
    </div>
  );
};
